import * as Mui from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useForm } from "react-hook-form";
import DamageFormService from "../../service/DamageFormService";
import useSnackbar from "../../../../hooks/useSnackbar";
import { useParams } from "react-router-dom";
import { DamageFormTypes } from "../../Types";
import FormInput from "../../../common/FormInput";
import { nameof } from "../../../common/ObjectUtils";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ContextOperation } from "../../../../context/OperationContext";
import { ContextDamageForm } from "../../DamageFormContext";
import { AutoCompleteOption } from "../../../common/AutoComplete";
import useOperationTranslation from "../../../../hooks/useOperationTranslation";
import useDamageFormActions from "../../service/DamageFormActions";
import { useTranslation } from "react-i18next";

interface Props {
  open: boolean;
  onClose: () => void;
}
const NewMessages = (props: Props) => {
  const { tenant, setLoading } = useContext(ContextOperation);
  const damageFormContext = useContext(ContextDamageForm);

  const { i18n } = useTranslation();

  const t = useOperationTranslation();

  const snackBar = useSnackbar();

  const { id } = useParams();

  const form = useForm<DamageFormTypes.IMessageForm>({
    defaultValues: {
      possibleAttachments: [],
    },
  });

  const [mailTemplates, setMailTemplates] =
    useState<DamageFormTypes.IMailTemplate[]>();
  const [languages, setLanguages] = useState<AutoCompleteOption[]>([]);
  const [possibleAttachments, setPossibleAttachments] = useState<
    DamageFormTypes.IPossibleAttachments[]
  >([]);

  const actions = useDamageFormActions();
  
  const convertLanguage = (lang: string) => {

    if (lang === 'it-IT')
      return "it";
    else if (lang === 'en-EN')
      return "en";
    else if (lang === 'fr-FR')
      return "fr";
    else
      return i18n.language.toLowerCase();

  }

	const onResetForm = () => {

	  //getMailTemplates();

	  const templateCustom = mailTemplates?.find((temp) => (temp.code === 'CUSTOM' && temp.language === form.watch("language")));

	  setPossibleAttachments(templateCustom?.possibleAttachments || []);

	  const customPossibleAttachments = templateCustom?.possibleAttachments.map(
		(attach) => (attach.selected ? attach.customId : undefined)
	  );

	  form.reset({
		language: templateCustom?.language || convertLanguage(i18n.language),
		body: templateCustom?.body || "",
		damageFormId: id,
		subject: templateCustom?.subject || "",
		templateId: templateCustom?.id || 0,
		title: templateCustom?.title || "",
		to: templateCustom?.standardTo || "",
        code: templateCustom?.code || "",
		possibleAttachments: customPossibleAttachments || [],
	  });

	};


  const onClose = () => {
    onResetForm();
    props.onClose();
  };

  const setFirstTimeLang = () => {
    if (form.watch("language") === undefined) {
      form.setValue("language", convertLanguage(i18n.language));
    }
  };

  const sendMail = (data: DamageFormTypes.IMessageForm) => {
    if (!id) return;

    const language = form.watch("language") || "";

    setLoading(true);
    DamageFormService.details.messages.post.sendMail(language, data).then(
      (response) => {
        snackBar.show(
          "success",
          t(
            "damageForm.details.messages.dialogs.newMessage.snackbar.sent.success"
          )
        );
        onClose();
        actions.getTask2();
        setLoading(false);
      },
      (error) => {
        if (error.response.status == 400) {
          snackBar.show(
            "error",
            t(
              "damageForm.details.messages.dialogs.newMessage.snackbar.sent.400error"
            )
          );
        } else {
          snackBar.show(
            "error",
            t(
              "damageForm.details.messages.dialogs.newMessage.snackbar.sent.error"
            )
          );
        }

        setLoading(false);
      }
    );
  };

  const getMailTemplates = () => {
    const language = form.watch("language");

    if (!damageFormContext.generalInfo?.progressive || !language) return;

    DamageFormService.details.messages.get
      .mailTemplates(
        tenant,
        damageFormContext.generalInfo.progressive,
        language
      )
      .then((response) => {
        setMailTemplates(response);
      });
  };

  const getLanguages = () => {
    DamageFormService.details.messages.get
      .languages(tenant)
      .then((response) => {
        setLanguages(
          response.map((lang) => ({
            label: lang,
            value: lang,
          }))
        );
      });
  };

  useEffect(() => {
    getLanguages();
    setFirstTimeLang();
    getMailTemplates();
  }, [damageFormContext.generalInfo?.progressive, form.watch("language")]);

  useEffect(() => {
    form.setValue("language", convertLanguage(i18n.language));
  }, [i18n.language]);
  
  useEffect(() => {
    onResetForm();
  }, [mailTemplates]);


  /*useEffect(() => {
        getLanguages();
        
        console.log("LANGUAGE: " + form.watch("language"))

    }, []);*/

  const mailTemplateOptions = useMemo<AutoCompleteOption[]>(() => {
    return (
      mailTemplates?.map((template) => {
        return {
          label: template.title,
          value: template.id,
        };
      }) || []
    );
  }, [mailTemplates]);

  const onSaveTemplate = () => {
    if (!damageFormContext.generalInfo?.progressive) return;

    
    
    console.log("FORM LANGUAGE: " + JSON.stringify(form.getValues()));

    setLoading(true);
    DamageFormService.details.messages.post
      .saveMailTemplate(
        tenant,
        damageFormContext.generalInfo?.progressive,
        form.getValues()
      )
      .then(
        (response) => {
          getMailTemplates();
          form.setValue("templateId", response.id);
          snackBar.show(
            "success",
            t(
              "damageForm.details.messages.dialogs.newMessage.snackbar.template.success"
            )
          );
          setLoading(false);
        },
        () => {
          snackBar.show(
            "error",
            t(
              "damageForm.details.messages.dialogs.newMessage.snackbar.template.error"
            )
          );
          setLoading(false);
        }
      );
  };

  const onChangeAttachments = (val: string[]) => {
    if (val) {
      const attachments = possibleAttachments.filter((attach) =>
        val.includes(attach.customId)
      );

      form.setValue("possibleAttachments", val);
      form.setValue("attachmentsSelected", attachments);
    }
  };

  const onChangeTemplate = (val?: number) => {
    if (val) {
      const template = mailTemplates?.find((temp) => temp.id === val);

      setPossibleAttachments(template?.possibleAttachments || []);

      const thisPossibleAttachments = template?.possibleAttachments.map(
        (attach) => (attach.selected ? attach.customId : undefined)
      );
      const attachments = template?.possibleAttachments.filter(
        (attach) => attach.selected
      );

      form.reset({
        language: template?.language,
        body: template?.body,
        damageFormId: id,
        subject: template?.subject,
        to: template?.standardTo,
        title: template?.title,
        templateId: template?.id,
        code: template?.code,
        possibleAttachments: thisPossibleAttachments,
        attachmentsSelected: attachments,
      });


    } else {
      onResetForm();
    }
  };

  const possibleAttachmentsOptions = useMemo<AutoCompleteOption[]>(() => {
    return possibleAttachments.map((attachment) => ({
      label: attachment.name + ' - ' + attachment.label,
      value: attachment.customId,
    }));
  }, [possibleAttachments]);

  return (
    <Mui.Dialog open={props.open} onClose={onClose} fullWidth maxWidth="lg">
      <Mui.DialogTitle>
        <Mui.Grid container alignItems={"center"}>
          <Mui.Grid item>
            <Mui.Typography variant="h5">
              {t("damageForm.details.messages.dialogs.newMessage.title")}
            </Mui.Typography>
          </Mui.Grid>
          <Mui.Grid item marginLeft={"auto"}>
            <Mui.IconButton onClick={onClose}>
              <CloseIcon />
            </Mui.IconButton>
          </Mui.Grid>
        </Mui.Grid>
      </Mui.DialogTitle>

      <form onSubmit={form.handleSubmit((data) => sendMail(data))}>
        <Mui.DialogContent>
          <Mui.Grid container spacing={1}>
            <Mui.Grid item container md={4} xs={12} alignItems="center">
              <Mui.Grid item md={3} xs={12}>
                {t(
                  "damageForm.details.messages.dialogs.newMessage.labels.language"
                )}
              </Mui.Grid>

              <Mui.Grid item md={9} xs={12}>
                <FormInput
                  name={nameof<DamageFormTypes.IMessageForm>("language")}
                  type="select"
                  options={languages}
                  formInstance={form}
                  variant="outlined"
                />
              </Mui.Grid>
            </Mui.Grid>

            <Mui.Grid item md={7} xs={0} />

            <Mui.Grid item container md={6} xs={12} alignItems="center">
              <Mui.Grid item md={2} xs={12}>
                {t(
                  "damageForm.details.messages.dialogs.newMessage.labels.template"
                )}
              </Mui.Grid>

              <Mui.Grid md={10} xs={12}>
                <FormInput
                  name={nameof<DamageFormTypes.IMessageForm>("templateId")}
                  type="select"
                  options={mailTemplateOptions}
                  value={form.watch("templateId")}
                  onChange={onChangeTemplate}
                  //placeholder={t("damageForm.details.messages.dialogs.newMessage.labels.custom")}
                  variant="outlined"
                />
              </Mui.Grid>
            </Mui.Grid>

            <Mui.Grid item container md={6} xs={12} alignItems="center">
              <Mui.Grid item md={3} xs={12}>
                {t(
                  "damageForm.details.messages.dialogs.newMessage.labels.attachment"
                )}
              </Mui.Grid>

              <Mui.Grid item md={9} xs={12}>
                <FormInput
                  name={nameof<DamageFormTypes.IMessageForm>(
                    "possibleAttachments"
                  )}
                  type="select"
                  options={possibleAttachmentsOptions}
                  value={form.watch("possibleAttachments")}
                  onChange={onChangeAttachments}
                  variant="outlined"
                  multiple={true}
                />
              </Mui.Grid>
            </Mui.Grid>

            <Mui.Grid item md={6} xs={0} />

            <Mui.Grid item container xs={12} alignItems="center">
              <Mui.Grid item md={1} xs={12}>
                {t("damageForm.details.messages.dialogs.newMessage.labels.to")}
              </Mui.Grid>

              <Mui.Grid item md={11} xs={12}>
                <FormInput
                  name={nameof<DamageFormTypes.IMessageForm>("to")}
                  type="text"
                  variant="outlined"
                  fullWidth
                  formInstance={form}
                  validation= {{
                    required: ("damageForm.details.messages.dialogs.newMessage.toErrorMessage"),
                  }}
                />
              </Mui.Grid>
            </Mui.Grid>

            <Mui.Grid item container xs={12} alignItems="center">
              <Mui.Grid item md={1} xs={12}>
                {t(
                  "damageForm.details.messages.dialogs.newMessage.labels.subject"
                )}
              </Mui.Grid>
              <Mui.Grid item md={11} xs={12}>
                <Mui.TextField
                  variant="outlined"
                  multiline
                  fullWidth
                  {...form.register("subject")}
                />
              </Mui.Grid>
            </Mui.Grid>

            <Mui.Grid item container xs={12} alignItems="center">
              <Mui.Grid item md={1} xs={12}>
                {t(
                  "damageForm.details.messages.dialogs.newMessage.labels.message"
                )}
              </Mui.Grid>
              <Mui.Grid item md={11} xs={12}>
                <Mui.TextField
                  variant="outlined"
                  rows={5}
                  multiline
                  fullWidth
                  {...form.register("body")}
                />
              </Mui.Grid>
            </Mui.Grid>
          </Mui.Grid>
        </Mui.DialogContent>

        <Mui.DialogActions>
          <Mui.Grid container spacing={2}>
            <Mui.Grid item xs="auto">
              <Mui.Button
                variant="outlined"
                color="inherit"
                size="smallPlus"
                onClick={onSaveTemplate}
              >
                <Mui.Typography color="inherit" variant="h6">
                  {t(
                    "damageForm.details.messages.dialogs.newMessage.btnSaveTemplate"
                  )}
                </Mui.Typography>
              </Mui.Button>
            </Mui.Grid>

            <Mui.Grid item xs="auto" ml="auto">
              <Mui.Button variant="outlined" onClick={onClose} size="smallPlus">
                <Mui.Typography color={"inherit"} variant="h6">
                  {t(
                    "damageForm.details.messages.dialogs.newMessage.btnCancel"
                  )}
                </Mui.Typography>
              </Mui.Button>
            </Mui.Grid>

            <Mui.Grid item xs="auto">
              <Mui.Button type="submit" size="smallPlus">
                <Mui.Typography color={"inherit"} variant="h6">
                  {t("damageForm.details.messages.dialogs.newMessage.btnSend")}
                </Mui.Typography>
              </Mui.Button>
            </Mui.Grid>
          </Mui.Grid>
        </Mui.DialogActions>
      </form>
    </Mui.Dialog>
  );
};
export default NewMessages;
